import { makeAutoObservable, runInAction } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { MultiDepositSteps } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositSteps.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import { Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    validateAmountRequire,
    validateMaxDeposit,
    validateMinAmountNew,
} from 'src/domains/players/webview/components/ValidatorsNew';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { RemainingLimitState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/remainingLimit/RemainingLimit.state';
import { amountViewToValue } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/amountViewToValue';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { billingInfoFieldsValidationDeposit } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/billingInfoFiledsValidation/realexBillingInfoFieldsValidation';
import { DepositErrorMessagesState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';
import {
    isAccountDepositBlocked,
    refreshAccountBasicDataForDeposit,
    syncAccountDepositBlockingErrors,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/accountDepositBlocking';
import {
    YaspaState,
    DepositYaspaPageType,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment.state';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { getDefaultPaymentMethod } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/PaymentMethodsConfig/paymentMethodsConfig';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { WorldpayTabState } from '../WorldpayTab/WorldpayTab.state';

export type PaymentMethodType = 'debit-card' | 'apple-pay' | 'google-pay' | 'yaspa';

export class MultiDepositTabState {
    public stepsState: MultiDepositSteps;
    public isLoading: boolean = false;
    public showMoreRingFenceFlag: boolean = false;

    public readonly depositAmount: FormInputState<string, Amount>;
    public readonly selectedPaymentMethod: FormInputState<PaymentMethodType, PaymentMethodType>;
    public readonly accountHelperMail: string;
    public readonly remainingLimitState: RemainingLimitState;
    public readonly errorMessagesState: DepositErrorMessagesState;

    private readonly languagesState: LanguagesState;
    private readonly minDepositAmount: Amount;
    private readonly basicDataModel: BasicDataModel;
    private readonly usersState: UsersState;
    private readonly configComponents: ConfigComponents;
    private readonly account: AccountState;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly localStorageState: LocalStorageState;

    public static get = AutoWeakMap.create(
        (common: Common, isSignup: boolean) => new MultiDepositTabState(common, isSignup)
    );

    public constructor(
        private readonly common: Common,
        public readonly isSignup: boolean
    ) {
        this.localStorageState = LocalStorageState.get(common);

        this.configComponents = ConfigComponents.get(common);
        this.accountHelperMail = this.configComponents.config.accountHelperMail;
        this.minDepositAmount = new Amount(this.configComponents.config.minDepositAmount);
        this.basicDataModel = BasicDataModel.get(common);
        this.languagesState = LanguagesState.get(common);
        this.usersState = UsersState.get(common);
        this.remainingLimitState = RemainingLimitState.get(common);
        this.errorMessagesState = new DepositErrorMessagesState(common, this.languagesState);
        this.stepsState = new MultiDepositSteps();
        this.account = AccountState.get(common);
        this.googleTagManager = GoogleTagManagerState.get(common);

        this.depositAmount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(
                validateMinAmountNew(
                    this.minDepositAmount,
                    this.languagesState.getTranslation(
                        'errors.min-deposit',
                        'Minimum deposit amount is {currencySymbol}{minValue}',
                        {
                            currencySymbol: BasicDataModel.get(this.common).moneySymbol,
                            minValue: this.minDepositAmount.value,
                        }
                    )
                )
            )
            .map(
                validateMaxDeposit(
                    () => this.remainingLimitState.remainingLimit ?? null,
                    () => this.basicDataModel.currency,
                    this.languagesState,
                    () => this.basicDataModel.basicDataReady?.depositRestriction,
                    this.accountHelperMail,
                    () => this.remainingLimitState.bindingLimitCalculationType
                )
            );

        const storedPaymentMethod = this.localStorageState.paymentMethod.getValue();

        const isLegal = this.checkIfMethodIsLegal(storedPaymentMethod);

        const defaultPaymentMethod = getDefaultPaymentMethod(common);

        const defaultSelected = storedPaymentMethod !== null && isLegal ? storedPaymentMethod : defaultPaymentMethod;

        this.selectedPaymentMethod = FormInputState.new<PaymentMethodType>(defaultSelected).map(
            (value): Result<PaymentMethodType> => {
                return Result.createOk(value);
            }
        );

        makeAutoObservable(this);

        void refreshAccountBasicDataForDeposit(this.common);
    }

    private checkIfMethodIsLegal = (method: 'debit-card' | 'apple-pay' | 'google-pay' | 'yaspa' | null): boolean => {
        const hasApplePay = this.configComponents.config.paymentProviders.applePay !== null;
        const hasGooglePay = this.configComponents.config.paymentProviders.googlePay !== null;
        const hasYaspa =
            this.configComponents.config.paymentProviders.openBanking !== null &&
            this.configComponents.config.paymentProviders.openBanking.allowOnlyWithdraw !== true;
        if (method === 'apple-pay') {
            return hasApplePay;
        } else if (method === 'google-pay') {
            return hasGooglePay;
        } else if (method === 'yaspa') {
            return hasYaspa;
        }
        return true;
    };

    public get showBalance(): string | undefined {
        const playableBalance = this.usersState.walletData.valueReady?.playableBalance;

        if (playableBalance !== undefined) {
            return this.basicDataModel.money(new Amount(playableBalance));
        }
    }

    public setMethodToLocalStore = (method: 'debit-card' | 'apple-pay' | 'google-pay' | 'yaspa'): void => {
        this.localStorageState.paymentMethod.setValue(method);
    };
    public onChange = (): void => {
        const formatValue = amountViewToValue(this.depositAmount.value);
        this.depositAmount.setValue(formatValue);
        this.syncDepositBlockingErrors();
    };

    public setAmount = (): void => {
        const amount = parseFloat(this.depositAmount.value);
        if (isNaN(amount) === true || amount === 0) {
            this.depositAmount.setValue('');
            this.syncDepositBlockingErrors();
            return;
        }
        this.depositAmount.setValue(amount.toFixed(2));
        this.syncDepositBlockingErrors();
    };

    public handleAdditionsChange = (amount: Amount): void => {
        this.depositAmount.setValue(amount.value);
        this.depositAmount.setAsVisited();
        this.syncDepositBlockingErrors();
    };

    public onSetInputToEmpty = (): void => {
        this.depositAmount.reset();
    };

    public get hasInputValue(): boolean {
        return this.depositAmount.value !== '';
    }

    public get isButtonDisabled(): boolean {
        if (this.depositAmount.result.value.type === 'error') {
            return true;
        }

        void this.basicDataModel.basicDataReady?.depositRestriction;
        void this.basicDataModel.basicDataReady?.playBreakExpiry;
        if (isAccountDepositBlocked(this.common, this.hasInputValue)) {
            return true;
        }
        return !this.hasInputValue || this.isLoading;
    }

    public get formattedAmount(): string {
        if (this.depositAmount.value !== '') {
            return this.basicDataModel.moneySymbol + this.depositAmount.value;
        }
        return `${this.basicDataModel.moneySymbol}0.00`;
    }

    public redirectToSelectPaymentMethod = (): void => {
        this.stepsState.redirectToSelectPaymentMethod();
    };

    public hideDepositSuccess = (): void => {
        this.stepsState.redirectToSetAmount();
        this.depositAmount.reset();
        this.selectedPaymentMethod.setValue('debit-card');
    };

    public submitDepositForm = async (): Promise<void> => {
        this.syncDepositBlockingErrors();
        this.depositAmount.setAsVisited();
        this.selectedPaymentMethod.setAsVisited();

        if (this.depositAmount.result.value.type === 'error' || !this.hasInputValue) {
            return;
        }

        if (isAccountDepositBlocked(this.common, this.hasInputValue)) {
            return;
        }

        if (this.selectedPaymentMethod.result.value.type === 'error') {
            return;
        }

        const paymentMethod = this.selectedPaymentMethod.value;
        const amount = new Amount(this.depositAmount.value);
        const oldFormatAmount = this.configComponents.precision.valueOldFormat(amount);
        const account = this.account.account;

        if (account === null) {
            console.error('submitDepositForm - User is anonymous');
            return;
        }

        const billingInfo = billingInfoFieldsValidationDeposit(this.basicDataModel.basicDataReady);
        if (billingInfo?.type !== 'ok') {
            console.error('submitDepositForm - No basic user data');
            return;
        }

        this.isLoading = true;

        try {
            if (this.isSignup) {
                this.googleTagManager.depositedMoney(oldFormatAmount, true);
                this.googleTagManager.gtmSignUpStepFour(oldFormatAmount);
            } else {
                this.googleTagManager.depositedMoney(oldFormatAmount, false);
            }

            if (paymentMethod === 'apple-pay') {
                runInAction(() => {
                    this.isLoading = false;
                });
                if (this.configComponents.config.paymentProviders.applePay?.type === 'worldpay') {
                    const worldpayTabState = new WorldpayTabState(
                        this.common,
                        this.depositAmount.value,
                        'apple-pay',
                        this.stepsState,
                        this.isSignup,
                        this.errorMessagesState
                    );
                    await worldpayTabState.startApplePayPayment();
                } else {
                    this.stepsState.redirectToPaysafeLoader(this.depositAmount.value);
                }
                return;
            }
            if (paymentMethod === 'google-pay') {
                runInAction(() => {
                    this.isLoading = false;
                });
                if (this.configComponents.config.paymentProviders.debitCard.type === 'worldpay') {
                    const worldpayTabState = new WorldpayTabState(
                        this.common,
                        this.depositAmount.value,
                        'google-pay',
                        this.stepsState,
                        this.isSignup,
                        this.errorMessagesState
                    );
                    await worldpayTabState.startGooglePayPayment();
                }
                return;
            }
            if (paymentMethod === 'yaspa') {
                try {
                    const depositYaspaPage: DepositYaspaPageType = {
                        isSignup: this.isSignup,
                        getAmountInput: () => this.depositAmount,
                        handleRedirectToYaspaFrame: (link) => {
                            runInAction(() => {
                                this.isLoading = false;
                            });
                            this.stepsState.redirectToIframeYaspa({ link });
                        },
                    };

                    const yaspaState = new YaspaState(
                        'deposit',
                        this.common,
                        depositYaspaPage,
                        this.usersState,
                        this.errorMessagesState
                    );
                    await yaspaState.submitDepositForm();
                } catch {
                    runInAction(() => {
                        this.isLoading = false;
                    });
                    this.errorMessagesState.createErrorMessage('unknown');
                    this.stepsState.redirectToSetAmount();
                }
                return;
            }
            // last option is paymentMethod === 'debit-card'
            runInAction(() => {
                this.isLoading = false;
            });
            if (this.configComponents.config.paymentProviders.debitCard.type === 'worldpay') {
                this.stepsState.redirectToWorldpayLoader(this.depositAmount.value, 'debit-card');
            } else {
                this.stepsState.redirectToRealexLoader(this.depositAmount.value);
            }
            return;
        } catch (error) {
            this.stepsState.redirectToFailureView('serverIssue');
            throw error;
        } finally {
            this.isLoading = false;
        }
    };

    /* ringFencedFundsFlag start */
    public toggleShowMore = (): void => {
        this.showMoreRingFenceFlag = !this.showMoreRingFenceFlag;
    };

    public get hasRingFencedFunds(): boolean {
        return this.basicDataModel.basicDataReady?.ringFencedFunds ?? false;
    }
    /* ringFencedFundsFlag end*/

    private syncDepositBlockingErrors = (): void => {
        syncAccountDepositBlockingErrors(this.common, this.errorMessagesState, this.hasInputValue);
    };
}
